import { useState, useMemo, useCallback } from "react";
import { DeveloperTrack } from "../types";
import { useDeveloperUsers } from "./use-developer-users.hook";
import { useAssignDeveloperUser } from "./use-assign-developer-user.hook";

interface UseEditDeveloperUserProps {
  track: DeveloperTrack | null;
  onSuccess?: () => void;
}

interface UseEditDeveloperUserReturn {
  // Modal state
  isOpen: boolean;
  openModal: (track: DeveloperTrack) => void;
  closeModal: () => void;
  
  // Current track
  currentTrack: DeveloperTrack | null;
  
  // Selected user
  selectedUserUId: string | null;
  setSelectedUserUId: (uid: string | null) => void;
  
  // Users data
  users: { uid: string; name: string; phoneNumber?: string }[];
  usersLoading: boolean;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  
  // Submit
  handleSubmit: () => Promise<void>;
  isSubmitting: boolean;
  isValid: boolean;
  
  // Has changes
  hasChanges: boolean;
}

const useEditDeveloperUser = ({
  onSuccess,
}: UseEditDeveloperUserProps = { track: null }): UseEditDeveloperUserReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentTrack, setCurrentTrack] = useState<DeveloperTrack | null>(null);
  const [selectedUserUId, setSelectedUserUId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch developer users from API
  const {
    data: usersData,
    isLoading: usersLoading,
  } = useDeveloperUsers({
    developerUId: currentTrack?.developerUId || "",
    search: searchTerm,
    enabled: isOpen && !!currentTrack?.developerUId,
  });

  // Assign developer user mutation
  const assignMutation = useAssignDeveloperUser({
    trackUId: currentTrack?.trackUId || "",
    onSuccess: () => {
      closeModal();
      onSuccess?.();
    },
  });

  // Transform users to expected format
  const users = useMemo(() => {
    if (!usersData?.results) return [];
    return usersData.results.map((user) => ({
      uid: user.uid,
      name: user.name,
      phoneNumber: user.phoneNumber,
    }));
  }, [usersData]);

  const openModal = useCallback((track: DeveloperTrack) => {
    setCurrentTrack(track);
    // Set current agent as selected
    setSelectedUserUId(track.agentUId || null);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      setCurrentTrack(null);
      setSelectedUserUId(null);
      setSearchTerm("");
    }, 300);
  }, []);

  // Check if selection has changed
  const hasChanges = useMemo(() => {
    if (!currentTrack) return false;
    return selectedUserUId !== currentTrack.agentUId;
  }, [currentTrack, selectedUserUId]);

  // Form is valid if a user is selected
  const isValid = useMemo(() => {
    return selectedUserUId !== null;
  }, [selectedUserUId]);

  const handleSubmit = useCallback(async () => {
    if (!isValid || !currentTrack || !selectedUserUId) return;

    await assignMutation.mutateAsync({
      developerUserUId: selectedUserUId,
    });
  }, [isValid, currentTrack, selectedUserUId, assignMutation]);

  return {
    isOpen,
    openModal,
    closeModal,
    currentTrack,
    selectedUserUId,
    setSelectedUserUId,
    users,
    usersLoading,
    searchTerm,
    setSearchTerm,
    handleSubmit,
    isSubmitting: assignMutation.isLoading,
    isValid,
    hasChanges,
  };
};

export default useEditDeveloperUser;
